import { AccountBanner } from './AccountBanner'

/**
 * @typedef {import("../types/banner.ts").OfferBanners} OfferBanners
 * @typedef {import("../types/banner.ts").SplitBannerType} SplitBannerType
 * @typedef {import("../types/banner.ts").SplitSideBannerType} SplitSideBannerType
 * @typedef {import("../types/banner.ts").ImageBannerType} ImageBannerType

 */

const FullBanner = ({ banner }) => {
  return (
    <div className={'py-4 perspective-0'}>
      <AccountBanner
        className="-rotate-1 bg-secondary-violet/30"
        title={banner.title}
        cta={{
          title: banner.cta.title,
          callback: () => {
            window.location.assign(banner.cta.url)
          }
        }}
      />
    </div>
  )
}
const isValidImageUrl = (img) => {
  if (!img || !img.length) return false
  if (img.includes('no-image')) return false
  return true
}

const handleContentPosition = (position) => {
  switch (position) {
    case 'top_left': {
      return 'top-0'
    }
    case 'center_left': {
      return 'inset-0'
    }
    case 'bottom_left': {
      return 'bottom-0'
    }
    case 'top_center': {
      return 'w-fit top-0 left-0 right-0 m-auto'
    }
    case 'center': {
      return 'w-fit inset-0 m-auto'
    }
    case 'bottom_center': {
      return 'w-fit bottom-0 left-0 right-0 m-auto'
    }
    case 'top_right': {
      return 'w-fit top-0 right-0 m-auto'
    }
    case 'center_right': {
      return 'w-fit top-0 right-0 bottom-0 m-auto'
    }
    case 'bottom_right': {
      return 'w-fit right-0 bottom-0 m-auto'
    }
    default: {
      return 'inset-0 w-full'
    }
  }
}

const handleBackgroundColor = (color) => {
  switch (color) {
    case 'white': {
      return 'bg-primary-white'
    }
    case 'black': {
      return 'bg-primary-black'
    }
    case 'blue': {
      return 'bg-primary-blue'
    }
    case 'sale': {
      return 'bg-primary-sale'
    }
    case 'green': {
      return 'bg-secondary-green'
    }
    case 'violet': {
      return 'bg-secondary-violet'
    }
    case 'yellow': {
      return 'bg-secondary-yellow'
    }
    case 'palepink': {
      return 'bg-secondary-palepink'
    }
    case 'paleblue': {
      return 'bg-secondary-paleblue'
    }
    default: {
      return 'bg-primary-black'
    }
  }
}

const handleTextColor = (color) => {
  switch (color) {
    case 'white': {
      return 'text-primary-white'
    }
    case 'black': {
      return 'text-primary-black'
    }
    case 'blue': {
      return 'text-primary-blue'
    }
    case 'sale': {
      return 'text-primary-sale'
    }
    case 'green': {
      return 'text-secondary-green'
    }
    case 'violet': {
      return 'text-secondary-violet'
    }
    case 'yellow': {
      return 'text-secondary-yellow'
    }
    case 'palepink': {
      return 'text-secondary-palepink'
    }
    case 'paleblue': {
      return 'text-secondary-paleblue'
    }
    default: {
      return 'text-primary-black'
    }
  }
}

/**
 * @param {{side: SplitSideBannerType}} param0
 */
const SplitBannerSide = ({ side }) => {
  const textPosition = handleContentPosition(side.text_position)
  const textColor = handleTextColor(side.text_colour)
  const validCTA = side.cta.url?.length && side.cta.title?.length
  return (
    <div className={'relative aspect-4/3 h-full w-full p-6'}>
      {isValidImageUrl(side.image) ? <img src={side.image} className={'h-full w-full object-cover'} /> : <></>}
      <div className={`absolute ${textPosition} flex  flex-col justify-center gap-4 p-6 pb-10`}>
        <h2 className={`h2 font-bold ${textColor}`}>{side.title}</h2>
        <div className={`paragraph-md richtext ${textColor}`} dangerouslySetInnerHTML={{ __html: side.body }}></div>
        <div>
          {validCTA ? (
            <a className={'btn btn-md btn-solid-black w-fit'} href={side.cta.url}>
              {side.cta.title}
            </a>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * @param {{banner: SplitBannerType}} param0
 */
const SplitBanner = ({ banner }) => {
  const backgroundColor = handleBackgroundColor(banner.background)
  const flipped = ' flex flex-col md:flex-row'
  return (
    <div className={`${backgroundColor} md:h-96 ${flipped}`}>
      <SplitBannerSide side={banner.left} />
      <SplitBannerSide side={banner.right} />
    </div>
  )
}

/**
 *
 * @param {{banner: ImageBannerType}} param0
 * @returns
 */
const ImageBanner = ({ banner }) => {
  return (
    <a href={banner.callback}>
      <div className={'aspect-1 min-h-96 w-full'}>
        <img className={'h-full w-full object-cover'} src={banner.image} />
      </div>
    </a>
  )
}

/**
 * @param {{banners: OfferBanners}} param0
 */
const BannerRenderFactory = ({ banners }) => {
  return (
    <>
      {banners.map((banner) => {
        if (banner.type === 'full_banner') {
          return <FullBanner banner={banner} />
        } else if (banner.type === 'split_banner') {
          return <SplitBanner banner={banner} />
        } else if (banner.type === 'image_banner') {
          return <ImageBanner banner={banner} />
        }
        return <></>
      })}
    </>
  )
}

/**
 * The data comes set from the account-block.liquid file.
 * The global_member_offers definitions are set from the
 * Theme section blocks.
 *
 * @returns
 */
export const AccountOffers = () => {
  // eslint-disable-next-line camelcase
  const banners = global_member_offers
  return (
    <div className={'flex flex-col gap-4'}>
      <BannerRenderFactory banners={banners} />
    </div>
  )
}
